import { sendToTerminal } from '../terminalLogger';

export const TOOLS_SESSION_READY_EVENT = 'oneprint:tools-session-ready';

const TOOLS_SESSION_GUARD_BYPASS = new Set<string>([
  'app-status',
  'health:pong',
  'notificar-webview-disponivel',
  'session:hello',
  'session:request',
  'session:shared:ack',
  'frontend-ready',
  'tools:frontend-ready',
  'tools:ready-for-session',
  'tools:session-applied',
  'shortcuts:disable',
  'shortcuts:enable',
  'shortcuts:list'
]);

function getWindowState() {
  return window as any;
}

export function isToolsDockerContext(): boolean {
  const win = getWindowState();
  const path = window.location.pathname || '';
  const dockerId = String(win.dockerId || '');
  return path.startsWith('/tools') || path.startsWith('/embedded/tools') || dockerId.startsWith('tools-');
}

export function shouldBypassToolsSessionGuard(action: string): boolean {
  return TOOLS_SESSION_GUARD_BYPASS.has(action);
}

export function getToolsSessionReadyState(): {
  ready: boolean;
  reason: string | null;
  updatedAt: number | null;
} {
  const win = getWindowState();
  return {
    ready: !!win.__ONEPRINT_TOOLS_SESSION_READY__,
    reason: win.__ONEPRINT_TOOLS_SESSION_REASON__ || null,
    updatedAt: typeof win.__ONEPRINT_TOOLS_SESSION_UPDATED_AT__ === 'number'
      ? win.__ONEPRINT_TOOLS_SESSION_UPDATED_AT__
      : null
  };
}

export function setToolsSessionReady(
  ready: boolean,
  reason: string,
  extra: Record<string, any> = {}
): void {
  const win = getWindowState();
  const prev = getToolsSessionReadyState();
  const nextUpdatedAt = Date.now();

  win.__ONEPRINT_TOOLS_SESSION_READY__ = ready;
  win.__ONEPRINT_TOOLS_SESSION_REASON__ = reason;
  win.__ONEPRINT_TOOLS_SESSION_UPDATED_AT__ = nextUpdatedAt;

  sendToTerminal(
    ready
      ? '✅ [TOOLS_SESSION] Sessão do tools pronta'
      : '⏳ [TOOLS_SESSION] Sessão do tools pendente',
    {
      ready,
      reason,
      previousReady: prev.ready,
      previousReason: prev.reason,
      updatedAt: nextUpdatedAt,
      dockerId: win.dockerId || null,
      path: window.location.pathname,
      ...extra
    }
  );

  window.dispatchEvent(new CustomEvent(TOOLS_SESSION_READY_EVENT, {
    detail: {
      ready,
      reason,
      updatedAt: nextUpdatedAt,
      dockerId: win.dockerId || null,
      ...extra
    }
  }));
}

export async function waitForToolsSessionReady(
  timeoutMs: number = 5000,
  extra: Record<string, any> = {}
): Promise<{
  ready: boolean;
  reason: string | null;
  updatedAt: number | null;
  waitedMs: number;
}> {
  const initial = getToolsSessionReadyState();
  if (initial.ready) {
    sendToTerminal('ℹ️ [TOOLS_SESSION] Sessão já estava pronta para a action', {
      timeoutMs,
      waitedMs: 0,
      reason: initial.reason,
      updatedAt: initial.updatedAt,
      dockerId: (window as any).dockerId || null,
      path: window.location.pathname,
      ...extra
    });

    return {
      ready: true,
      reason: initial.reason,
      updatedAt: initial.updatedAt,
      waitedMs: 0
    };
  }

  const startedAt = Date.now();
  sendToTerminal('⏳ [TOOLS_SESSION] Aguardando sessão do tools antes de executar action', {
    timeoutMs,
    reason: initial.reason,
    updatedAt: initial.updatedAt,
    dockerId: (window as any).dockerId || null,
    path: window.location.pathname,
    ...extra
  });

  return await new Promise((resolve) => {
    let settled = false;
    let timeoutId = 0;

    const finish = (ready: boolean, reason: string | null, updatedAt: number | null) => {
      if (settled) {
        return;
      }
      settled = true;

      try {
        window.removeEventListener(TOOLS_SESSION_READY_EVENT, handleReadyEvent as EventListener);
      } catch {}
      try {
        window.clearTimeout(timeoutId);
      } catch {}

      const waitedMs = Date.now() - startedAt;
      sendToTerminal(
        ready
          ? '✅ [TOOLS_SESSION] Sessão do tools liberada durante a espera'
          : '⚠️ [TOOLS_SESSION] Timeout aguardando sessão do tools',
        {
          timeoutMs,
          waitedMs,
          reason,
          updatedAt,
          dockerId: (window as any).dockerId || null,
          path: window.location.pathname,
          ...extra
        }
      );

      resolve({
        ready,
        reason,
        updatedAt,
        waitedMs
      });
    };

    const handleReadyEvent = (event: Event) => {
      const detail = (event as CustomEvent<any>).detail || {};
      if (detail.ready) {
        finish(true, detail.reason || null, detail.updatedAt ?? null);
        return;
      }

      sendToTerminal('🔄 [TOOLS_SESSION] Evento recebido, mas sessão ainda segue pendente', {
        timeoutMs,
        waitedMs: Date.now() - startedAt,
        detailReady: !!detail.ready,
        reason: detail.reason || null,
        updatedAt: detail.updatedAt ?? null,
        dockerId: detail.dockerId || (window as any).dockerId || null,
        path: window.location.pathname,
        ...extra
      });
    };

    window.addEventListener(TOOLS_SESSION_READY_EVENT, handleReadyEvent as EventListener);

    timeoutId = window.setTimeout(() => {
      const current = getToolsSessionReadyState();
      finish(!!current.ready, current.reason, current.updatedAt);
    }, timeoutMs);
  });
}
